import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Shield, User, Database, Share2, Lock, Trash2, CheckCircle, AlertCircle } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4">
      <Helmet>
        <title>Privacy Policy - Descriptive Test | EMRS KVS NVS Exam Preparation</title>
        <meta name="description" content="Privacy Policy for Descriptive Test platform. Learn how we collect, use, and protect your personal information on our EMRS, KVS, NVS exam preparation platform." />
        <link rel="canonical" href="https://descriptivetest.com/privacy-policy" />
      </Helmet>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/">
            <Button variant="outline" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>

        <Card className="shadow-xl">
          <CardHeader className="bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Shield className="w-6 h-6" />
              Privacy Policy
            </CardTitle>
            <p className="text-green-100 text-sm mt-1">Last updated: February 2025</p>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6 text-gray-700 leading-relaxed">
            
            {/* Introduction */}
            <section>
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-3">
                <User className="w-5 h-5 text-green-600" />
                Introduction
              </h3>
              <p>
                This Privacy Policy describes how <strong>Descriptive Test</strong> and its affiliates (collectively "Descriptive Test, we, our, us") collect, use, share, protect or otherwise process your information/personal data through our website <strong>https://descriptivetest.com/</strong> (hereinafter referred to as Platform).
              </p>
              <p className="mt-3">
                Please note that you may be able to browse certain sections of the Platform without registering with us. We do not offer any product/service under this Platform outside India and your personal data will primarily be stored and processed in India.
              </p>
              <p className="mt-3">
                By visiting this Platform, providing your information or availing any product/service offered on the Platform, you expressly agree to be bound by the terms and conditions of this Privacy Policy, the Terms of Use and the applicable service/product terms and conditions, and agree to be governed by the laws of India including but not limited to the laws applicable to data protection and privacy. If you do not agree please do not use or access our Platform.
              </p>
            </section>

            {/* Collection */}
            <section className="bg-blue-50 p-5 rounded-lg">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-3">
                <Database className="w-5 h-5 text-blue-600" />
                Collection of Information
              </h3>
              <p>
                We collect your personal data when you use our Platform, services or otherwise interact with us during the course of our relationship. Some of the information that we may collect includes but is not limited to:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Personal data/information provided during sign-up/registering such as <strong>name, date of birth, address, telephone/mobile number, email ID</strong></li>
                <li>Any information shared as proof of identity or address</li>
                <li>Sensitive personal data collected with your consent, such as bank account or credit/debit card or other payment instrument information</li>
                <li>Biometric information such as facial features or physiological information (when opted for certain features)</li>
              </ul>
              <p className="mt-3">
                You always have the option to not provide information, by choosing not to use a particular service or feature on the Platform. We may track your behaviour, preferences, and other information that you choose to provide on our Platform. This information is compiled and analysed on an aggregated basis.
              </p>
              <div className="mt-4 p-3 bg-red-100 rounded-lg border border-red-300">
                <p className="text-red-800 text-sm flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>Warning:</strong> If you receive an email or call from a person/association claiming to be Descriptive Test seeking any personal data like debit/credit card PIN, net-banking or mobile banking password, we request you to never provide such information. If you have already revealed such information, report it immediately to an appropriate law enforcement agency.</span>
                </p>
              </div>
            </section>

            {/* Usage */}
            <section className="bg-purple-50 p-5 rounded-lg">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-purple-600" />
                Usage of Information
              </h3>
              <p>We use personal data to provide the services you request. To the extent we use your personal data to market to you, we will provide you the ability to opt-out of such uses. We use your personal data to:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Assist sellers and business partners in handling and fulfilling orders</li>
                <li>Enhance customer experience</li>
                <li>Resolve disputes and troubleshoot problems</li>
                <li>Inform you about online and offline offers, products, services, and updates</li>
                <li>Customise your experience</li>
                <li>Detect and protect against error, fraud and other criminal activity</li>
                <li>Enforce our terms and conditions</li>
                <li>Conduct marketing research, analysis and surveys</li>
              </ul>
            </section>

            {/* Sharing */}
            <section className="bg-orange-50 p-5 rounded-lg">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-3">
                <Share2 className="w-5 h-5 text-orange-600" />
                Sharing of Information
              </h3>
              <p>We may share your personal data:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Internally within our group entities, corporate entities, and affiliates to provide you access to services and products</li>
                <li>With third parties such as sellers, business partners, logistics partners, payment instrument issuers</li>
                <li>To government agencies or law enforcement agencies if required by law</li>
                <li>To enforce our Terms of Use or Privacy Policy</li>
                <li>To respond to claims that content violates rights of a third party</li>
                <li>To protect the rights, property or personal safety of our users or the general public</li>
              </ul>
            </section>

            {/* Security */}
            <section className="bg-green-50 p-5 rounded-lg">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-3">
                <Lock className="w-5 h-5 text-green-600" />
                Security Precautions
              </h3>
              <p>
                To protect your personal data from unauthorised access or disclosure, loss or misuse we adopt reasonable security practices and procedures. Once your information is in our possession or whenever you access your account information, we adhere to our security guidelines to protect it against unauthorised access and offer the use of a secure server.
              </p>
              <p className="mt-3">
                However, the transmission of information is not completely secure for reasons beyond our control. By using the Platform, the users accept the security implications of data transmission over the internet and the World Wide Web which cannot always be guaranteed as completely secure. Users are responsible for ensuring the protection of login and password records for their account.
              </p>
            </section>

            {/* Data Deletion */}
            <section className="bg-red-50 p-5 rounded-lg">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-3">
                <Trash2 className="w-5 h-5 text-red-600" />
                Data Deletion and Retention
              </h3>
              <p>
                You have an option to delete your account by visiting your profile and settings on our Platform. This action would result in you losing all information related to your account. You may also write to us at the contact information provided below to assist you with these requests.
              </p>
              <p className="mt-3">
                We may in event of any pending grievance, claims, pending shipments or any other services refuse or delay deletion of the account. Once the account is deleted, you will lose access to the account.
              </p>
              <p className="mt-3">
                We retain your personal data information for a period no longer than is required for the purpose for which it was collected or as required under any applicable law. However, we may retain data related to you if we believe it may be necessary to prevent fraud or future abuse or for other legitimate purposes.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h3 className="text-lg font-bold text-gray-800 mb-3">Your Rights</h3>
              <p>
                You may access, rectify, and update your personal data directly through the functionalities provided on the Platform.
              </p>
            </section>

            {/* Consent */}
            <section>
              <h3 className="text-lg font-bold text-gray-800 mb-3">Consent</h3>
              <p>
                By visiting our Platform or by providing your information, you consent to the collection, use, storage, disclosure and otherwise processing of your information on the Platform in accordance with this Privacy Policy.
              </p>
              <p className="mt-3">
                You have an option to withdraw your consent that you have already provided by writing to the Grievance Officer at the contact information provided below. Please mention "Withdrawal of consent for processing personal data" in your subject line. However, please note that your withdrawal of consent will not be retrospective and will be in accordance with the Terms of Use, this Privacy Policy, and applicable laws.
              </p>
            </section>

            {/* Changes */}
            <section>
              <h3 className="text-lg font-bold text-gray-800 mb-3">Changes to this Privacy Policy</h3>
              <p>
                Please check our Privacy Policy periodically for changes. We may update this Privacy Policy to reflect changes to our information practices. We may alert/notify you about the significant changes to the Privacy Policy, in the manner as may be required under applicable laws.
              </p>
            </section>

            {/* Grievance Officer */}
            <section className="bg-gray-100 p-5 rounded-lg">
              <h3 className="text-lg font-bold text-gray-800 mb-3">Grievance Officer</h3>
              <div className="space-y-2">
                <p><strong>Name:</strong> Lokesh Bhardwaj</p>
                <p><strong>Designation:</strong> Founder</p>
                <p><strong>Contact:</strong> 8076623513</p>
                <p><strong>Working Hours:</strong> Monday - Friday (9:00 AM - 6:00 PM)</p>
              </div>
            </section>

            {/* Navigation to other policies */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-4">Related Policies:</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/terms">
                  <Button variant="outline" size="sm">Terms & Conditions</Button>
                </Link>
                <Link to="/refund-policy">
                  <Button variant="outline" size="sm">Refund Policy</Button>
                </Link>
              </div>
            </div>

          </CardContent>
        </Card>
      </div>
    </div>
  );
}
